import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { descStyle } from "@/lib/fonts";
import { Lightbox } from "@/components/Lightbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PLACEHOLDER_IMAGE } from "@/lib/data";
import { Calendar, Clock, MapPin, Ticket, Globe, ArrowLeft, Building2, Expand } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [lb, setLb] = useState(null); // {images, index}

  useEffect(() => { api.get(`/events/${id}`).then((r) => setE(r.data)).catch(() => setE(false)); }, [id]);

  if (e === false) return <div className="max-w-3xl mx-auto p-20 text-center font-display text-2xl">Event not found</div>;
  if (!e) return <div className="p-20 text-center text-slate-400">Loading…</div>;

  const cover = e.image || e.business?.logo || PLACEHOLDER_IMAGE;
  const gallery = (e.gallery || []).filter(Boolean);
  const allImages = [cover, ...gallery];
  const mapSrc = e.lat && e.lng
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${e.lng - 0.01}%2C${e.lat - 0.008}%2C${e.lng + 0.01}%2C${e.lat + 0.008}&layer=mapnik&marker=${e.lat}%2C${e.lng}`
    : null;

  return (
    <div className="pb-16">
      <div className="relative h-64 md:h-[420px] group cursor-pointer" onClick={() => setLb({ images: allImages, index: 0 })} data-testid="event-cover">
        <img src={cover} alt={e.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 opacity-90 group-hover:opacity-100">
          <Expand className="w-4 h-4" /> View full
        </div>
        {e.is_city_event && <Badge className="absolute top-4 left-4 bg-orange-500 text-white rounded-full">Calgary Event</Badge>}
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 -mt-16 relative">
        <button onClick={() => navigate("/events")} className="text-white/90 text-sm flex items-center gap-1 mb-3 hover:text-white" data-testid="event-back">
          <ArrowLeft className="w-4 h-4" /> All events
        </button>
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge className="bg-orange-100 text-orange-700 rounded-full">{e.price === 0 ? "Free" : `$${e.price}`}</Badge>
            {e.business && <Link to={`/business/${e.business.id}`} className="text-sm text-slate-500 hover:text-orange-600 flex items-center gap-1"><Building2 className="w-4 h-4" /> {e.business.name}</Link>}
            {!e.business && e.organizer && <span className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="w-4 h-4" /> {e.organizer}</span>}
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">{e.title}</h1>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-slate-600 text-sm">
            {e.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-orange-500" /> {e.date}</span>}
            {e.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-500" /> {e.time}</span>}
            {e.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-orange-500" /> {e.location}</span>}
            <span className="flex items-center gap-1.5"><Ticket className="w-4 h-4 text-orange-500" /> {e.price === 0 ? "Free entry" : `$${e.price}`}</span>
          </div>

          {e.website && (
            <a href={e.website} target="_blank" rel="noreferrer" data-testid="event-website">
              <Button className="rounded-full bg-orange-500 hover:bg-orange-600 mt-5"><Globe className="w-4 h-4 mr-1.5" /> Event website</Button>
            </a>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            {e.description && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
                <h2 className="font-display text-xl font-semibold mb-4">About this event</h2>
                <div data-testid="event-description" className="text-slate-700" style={descStyle(e)}>{e.description}</div>
              </div>
            )}

            {gallery.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <h2 className="font-display text-xl font-semibold mb-4">Photos</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {gallery.map((g, i) => (
                    <img key={i} src={g} alt="" data-testid={`event-gallery-${i}`}
                      onClick={() => setLb({ images: allImages, index: i + 1 })}
                      className="aspect-square object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity" />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
              <h2 className="font-display text-xl font-semibold mb-4">Details</h2>
              <ul className="space-y-3 text-sm">
                {e.date && <li className="flex items-center gap-3"><Calendar className="w-4 h-4 text-orange-500" /> {e.date}{e.time ? `, ${e.time}` : ""}</li>}
                <li className="flex items-center gap-3"><Ticket className="w-4 h-4 text-orange-500" /> {e.price === 0 ? "Free" : `$${e.price}`}</li>
                {e.location && <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-orange-500" /> {e.location}</li>}
                {(e.organizer || e.business?.name) && <li className="flex items-center gap-3"><Building2 className="w-4 h-4 text-orange-500" /> {e.business?.name || e.organizer}</li>}
              </ul>
            </div>

            {mapSrc && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <h2 className="font-display text-xl font-semibold mb-4">Location</h2>
                <iframe title="map" src={mapSrc} className="w-full h-56 rounded-2xl border-0" data-testid="event-map" />
              </div>
            )}
          </div>
        </div>
      </div>

      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} />}
    </div>
  );
}
